import { useQuery } from "@tanstack/react-query";
import type { Channel } from "@kkp/api-client";
import { formatCurrency, formatDateTime } from "@kkp/app-kit";
import { getProfile } from "../../api/profile";

export const BANK_TRANSFER_UNAVAILABLE =
  "Bank Transfer is temporarily unavailable — use Virtual Account.";

export function PaymentDestinationBlock({ channel }: { channel: Channel }) {
  const isBank = channel === "Bank";
  const {
    data: profile,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["client-profile"],
    queryFn: getProfile,
    enabled: !isBank,
    refetchInterval: (query) =>
      query.state.data?.vaState === "open" ? 15_000 : false,
  });

  const vaName = profile?.vaName?.trim() || "—";
  const vaNumber = profile?.vaNumber?.trim() || "—";
  let vaStatus = "VA not ready — contact support";
  if (isPending) {
    vaStatus = "Loading VA details…";
  } else if (!isError && profile) {
    switch (profile.vaState) {
      case "disabled":
        vaStatus = "Opens when you upload a batch";
        break;
      case "paid":
        vaStatus = "Paid — funding confirmed";
        break;
      case "open":
        if (profile.vaAmountIdr !== null && profile.vaExpiresAt !== null) {
          vaStatus = `Amount: ${formatCurrency(profile.vaAmountIdr)} · Expires: ${formatDateTime(profile.vaExpiresAt, "yyyy-MM-dd HH:mm XXX")}`;
        }
        break;
    }
  }

  if (isBank) {
    return (
      <div
        aria-disabled="true"
        className="rounded-xl border border-white/10 bg-white/5 p-4"
      >
        <div className="text-sm font-semibold text-amber-200">
          {BANK_TRANSFER_UNAVAILABLE}
        </div>
        <div className="mt-2 grid gap-1 opacity-40 line-through select-none">
          <div className="text-xs text-white/70">
            BRI (002) · KCP Harapan Indah Bekasi
          </div>
          <div className="font-mono text-2xl font-bold tracking-wider text-white/70 break-all">
            0424-01-169169-30-1
          </div>
          <div className="text-xs font-semibold text-white/70">
            a/n PT. IMMER
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-200">
        Virtual Account (Payment Funding)
      </div>
      <div className="mt-2 grid gap-1">
        <div className="text-xs text-amber-100/80">BRI · Bank code 002</div>
        <div className="font-mono text-2xl font-bold tracking-wider text-amber-50 break-all">
          {vaNumber}
        </div>
        <div className="text-xs font-semibold text-amber-100">
          {`a/n ${vaName}`}
        </div>
        <div role="status" className="text-sm font-semibold text-amber-100">
          {vaStatus}
        </div>
      </div>
    </div>
  );
}
